import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectsService } from '../api/projects.service';
import { PROJECT_KEYS } from './queryKeys';
import type { Project } from '../types';

/**
 * Returns the flat list of active projects for the current tenant.
 *
 * @remarks
 * For paginated/infinite use in the sidebar, use `useProjectsInfinite` from shared/.
 * staleTime 120s — projects change infrequently.
 *
 * @returns Query result with `data: Project[]`, `isLoading`, `isError`
 */
export function useProjects(): ReturnType<typeof useQuery<Project[]>> {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useQuery({
    queryKey: PROJECT_KEYS.list(orgId ?? ''),
    queryFn: async ({ signal }) => {
      const result = await projectsService.getList(
        {
          IncludeArchived: false,
          Page: 1,
          PageSize: 100,
        },
        signal,
      );
      return result.items ?? [];
    },
    staleTime: 120_000,
    enabled: !!orgId,
  });
}
